<script lang="ts">
import type { Component } from 'vue'

export interface BannerProps {
  as?: string | Component
  text: string
  icon?: string
  color?: 'primary' | 'secondary' | 'warning' | 'critical' | 'success'
  isClosable?: boolean
  isLoading?: boolean
  actions?: { label: string, onClick?: () => void }[]
  class?: string | object
}

const ui = {
  root: 'inline-flex w-full items-center justify-center px-4 py-2 rounded-xl min-h-12 gap-2',
  base: 'flex items-center w-full justify-center gap-2',
  text: 'text-md font-medium',
  icon: 'text-muted shrink-0',
  wrapper: 'flex gap-2 flex-1',
  color: {
    root: {
      all: 'bg-{color}-50',
      default: 'text-{color}-600',
      secondary: 'text-secondary-900',
    },
  },
  close: 'text-muted shrink-0 cursor-pointer size-5',
  actions: 'flex items-center gap-2',
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed, ref, useSlots } from 'vue'
import icons from '../styles/icons'
import AwButton from './AwButton.vue'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const {
  as = 'span',
  text,
  icon,
  color = 'secondary',
  isClosable = false,
  isLoading = false,
  actions = [],
  class: propsClass,
} = defineProps<BannerProps>()

const slots = useSlots()

const isVisible = ref(true)

const rootClass = computed(() => [
  ui.root,
  propsClass,
  ui.color.root.all.replaceAll('{color}', color),
  color === 'secondary' ? ui.color.root.secondary : ui.color.root.default.replaceAll('{color}', color),
])

const iconClass = computed(() => [
  ui.icon,
  isLoading && 'animate-spin',
])

function onClose() {
  isVisible.value = false
}
</script>

<template>
  <Primitive v-if="isVisible" :as :class="rootClass">
    <div :class="[ui.wrapper, { 'justify-center items-center': !isClosable && !actions.length && !slots.action }]">
      <AwIcon v-if="icon" :name="icon" :class="iconClass" />
      <span :class="ui.text">{{ text }}</span>
    </div>
    <div :class="ui.actions">
      <AwButton
        v-for="action, index in actions"
        :key="index"
        :label="action.label"
        variant="outline"
        color="secondary"
        size="sm"
        @click="action.onClick"
      />
      <slot name="action" />
      <AwIcon v-if="isClosable" :name="icons.close" :class="ui.close" @click="onClose" />
    </div>
  </Primitive>
</template>
